<script>
import { required } from "@vuelidate/validators";
import { tel } from "@/utils/form-validators";

import Button from "@/components/atoms/button";
import FormInput from "@/components/molecules/form-input";
import FormGroup from "@/components/molecules/form-group";
import Form from "@/components/molecules/form";
import FormFieldset from "@/components/molecules/form-fieldset";
import FormRadio from "@/components/molecules/form-radio";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import StateOverlay from "@/components/molecules/state-overlay";

export default {
  name: "ModifierInformations",
  components: {
    Button,
    FormInput,
    FormGroup,
    Form,
    FormFieldset,
    FormRadio,
    StateOverlay
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { v$, getState, getErrorMessage, checkValidation } =
      useFormValidation(props);
    return {
      v$,
      getState,
      getErrorMessage,
      checkValidation
    };
  },
  data() {
    return {
      model: {
        civilite: "",
        nom: "",
        prenom: "",
        tel: "",
        optin_sms_suivi_commande_drive: false
      }
    };
  },
  validations() {
    return {
      model: {
        nom: {
          required
        },
        prenom: {
          required
        },
        civilite: {
          required
        },
        tel: {
          required,
          tel
        }
      }
    };
  },
  mounted() {
    if (this.client) {
      this.hydrateModel(this.client);
    }
  },
  methods: {
    hydrateModel(client) {
      this.model.civilite = client.civilite;
      this.model.nom = client.nom;
      this.model.prenom = client.prenom;
      this.model.tel = client.tel;
      this.model.optin_sms_suivi_commande_drive =
        client.optin_sms_suivi_commande_drive;
    },
    save() {
      if (this.checkValidation(this.v$)) {
        this.$emit("save", this.model);
      }
    }
  }
};
</script>

<template>
  <div>
    <h1 class="u-color-primary u-font-24 u-mb-md u-text-center">
      Modifier vos informations personnelles
    </h1>
    <Form
      class="u-mt-md"
      :error="error"
      @submit.prevent="save"
    >
      <StateOverlay
        :is-loading="isLoading"
        overlay
      >
        <FormFieldset
          center
          size="lg"
        >
          <label class="u-block u-font-13 u-mb-xs u-color-text-light">
            Civilité *
          </label>
          <FormGroup
            inline
            class="u-mb-md"
            :state="getState(v$,'civilite', 'model')"
          >
            <FormRadio
              v-model="model.civilite"
              name="civilite"
              value="Mme"
              required
            >
              Madame
            </FormRadio>
            <FormRadio
              v-model="model.civilite"
              name="civilite"
              value="M."
              required
              class="u-ml-lg"
            >
              Monsieur
            </FormRadio>
            <template #message>
              <p>
                {{ getErrorMessage("civilite","Merci de renseigner votre civilité") }}
              </p>
            </template>
          </FormGroup>

          <FormGroup
            class="u-mb-md"
            :state="getState(v$,'nom', 'model')"
          >
            <FormInput
              v-model="model.nom"
              type="text"
              :state="getState(v$,'nom', 'model')"
            >
              Nom *
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("nom","Merci de renseigner votre nom") }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            class="u-mb-md"
            :state="getState(v$,'prenom', 'model')"
          >
            <FormInput
              v-model="model.prenom"
              type="text"
              :state="getState(v$,'prenom', 'model')"
            >
              Prénom *
            </FormInput>
            <template #message>
              <p>
                {{ getErrorMessage("prenom","Merci de renseigner votre prénom") }}
              </p>
            </template>
          </FormGroup>
          <FormGroup
            class="u-mb-lg"
            :state="getState(v$,'tel', 'model')"
          >
            <FormInput
              v-model="model.tel"
              type="tel"
              :state="getState(v$,'tel', 'model')"
              @blur="v$.model.tel.$touch()"
              @input="v$.model.tel.$reset()"
            >
              Téléphone *
            </FormInput>
            <template #message>
              <p>
                <template v-if="v$.model.tel.required.$invalid">
                  Merci de renseigner votre numéro de téléphone
                </template>
                <template v-else>
                  {{ getErrorMessage("tel","Veuillez entrer un numéro de téléphone valide qui contient entre 6 et 20 caractères, y compris les espaces") }}
                </template>
              </p>
            </template>
          </FormGroup>
          <label class="u-block u-font-14 u-mb-xs">
            Souhaitez-vous être informé(e) par SMS de l'évolution de vos commandes ?
          </label>
          <FormGroup inline>
            <FormRadio
              v-model="model.optin_sms_suivi_commande_drive"
              name="sms_suivi_commande"
              :value="true"
              class="u-mr-lg"
            >
              Oui
            </FormRadio>
            <FormRadio
              v-model="model.optin_sms_suivi_commande_drive"
              name="sms_suivi_commande"
              :value="false"
            >
              Non
            </FormRadio>
          </FormGroup>
        </FormFieldset>
        <div class="u-text-center u-mt-lg u-mb-lg">
          <Button
            variant="primary"
            radius="full"
            size="md"
            type="submit"
            value="Mettre à jour mes informations"
          />
        </div>
      </StateOverlay>
    </Form>
  </div>
</template>

<style lang="scss" scoped>
</style>
